import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";

import axios from "axios";
import TipTapEditor from "./TipTapEditor";
import { validateRecipientField } from "../emailRecipients";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function ForwardDialogPro({
  open,
  onClose,
  onSend,
  fromEmail,
  email,
  forwardedHtml,
  forwardOriginalAttachments,
}) {
  const editorRef = useRef(null);
  const forwardedHtmlRef = useRef("");

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "" });

  const [initialHtml, setInitialHtml] = useState("");

  /* 🔥 NUEVO → adjuntos */
  const [attachments, setAttachments] = useState([]);
  const [originalAttachments, setOriginalAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  function decodeBase64Safe(b64) {
    if (!b64 || typeof b64 !== "string") return null;

    // Gmail usa base64 URL-SAFE
    let base64 = b64.replace(/-/g, "+").replace(/_/g, "/");

    // Padding
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) {
      base64 += "=".repeat(padding);
    }

    try {
      const raw = atob(base64);
      return Uint8Array.from(raw, (c) => c.charCodeAt(0));
    } catch (err) {
      console.error("Base64 decode ERROR:", err);
      return null;
    }
  }

  useEffect(() => {
    if (!open) return;

    setTo("");
    setSubject(email ? `Fwd: ${email.subject}` : "");
    setAttachments([]);

    if (forwardOriginalAttachments && forwardOriginalAttachments.length > 0) {
      const converted = forwardOriginalAttachments
        .map((a) => {
          const arr = decodeBase64Safe(a.dataBase64);
          if (!arr) return null;

          return new File([arr], a.filename || "archivo", {
            type: a.mimeType || "application/octet-stream",
          });
        })
        .filter(Boolean);

      setOriginalAttachments(converted);
    } else {
      setOriginalAttachments([]);
    }
  }, [open, email, forwardOriginalAttachments]);

  /* Cargar firmas */
  useEffect(() => {
    if (!fromEmail) return;

    axios.get(`${API}/signatures/${fromEmail}`).then((res) => {
      const main = res.data.signature_html || "";
      const reply = res.data.reply_signature_html || main;

      setSignatures({ main, reply });
    });
  }, [fromEmail]);

  useEffect(() => {
    forwardedHtmlRef.current = forwardedHtml || "";
  }, [forwardedHtml]);

  /* Solo la firma va al editor; el bloque reenviado se preserva sin pasar por TipTap */
  useEffect(() => {
    if (!open) return;

    const sig =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    if (!editorRef.current) {
      setInitialHtml(sig);
    } else {
      editorRef.current.commands.setContent(sig, false);
    }
  }, [open, signatures, selectedSignature]);

  /* Enviar email reenviado */
  const sendForward = async () => {
    if (isSending) return;
    if (!to.trim()) {
      alert("Debe indicar al menos un destinatario.");
      return;
    }

    const recipients = validateRecipientField(to);
    if (!recipients.ok) {
      alert(`❌ ${recipients.message}`);
      return;
    }

    const sigHtml = editorRef.current?.getHTML?.() || "";

    const form = new FormData();
    form.append("fromEmail", fromEmail);
    form.append("to", recipients.normalized);
    form.append("subject", subject);
    form.append("html", sigHtml);
    form.append("skipServerSignature", "true");
    if (email?.gmailId) {
      form.append("forwardSourceGmailId", email.gmailId);
    }

    attachments.forEach((file) => form.append("attachments", file));
    originalAttachments
      .filter((f) => !String(f.type || "").startsWith("image/"))
      .forEach((file) => form.append("attachments", file));

    setIsSending(true);
    try {
      await onSend(form);
      onClose();
    } catch (err) {
      console.error("Error reenviando:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Error reenviando el correo";
      alert(`❌ ${msg}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={isSending ? undefined : onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Reenviar mensaje
        <IconButton onClick={onClose} disabled={isSending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* PARA */}
        <TextField
          fullWidth
          size="small"
          label="Para"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="reply">Firma respuesta</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* 🔥 Botón adjuntar */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1, ...accentOutlinedSx }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>
        {/* 🔥 Adjuntos ORIGINALES */}
        {originalAttachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {originalAttachments.map((file) => (
              <Chip
                key={file.name}
                label={`📎 ${file.name}`}
                onDelete={() =>
                  setOriginalAttachments((prev) =>
                    prev.filter((f) => f.name !== file.name)
                  )
                }
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* 🔥 Mostrar chips */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file) => (
              <Chip
                key={file.name}
                label={
                  file.type.startsWith("image/")
                    ? `🖼️ ${file.name}`
                    : `📄 ${file.name}`
                }
                onDelete={() => removeAttachment(file.name)}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* Firma / texto nuevo (editable) */}
        <DropZoneWrapper
          onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
        >
          <TipTapEditor
            editorRef={editorRef}
            initialHtml={initialHtml}
            onChange={() => {}}
          />
        </DropZoneWrapper>

        {/* Mensaje reenviado intacto (tablas, imágenes inline, etc.) */}
        {forwardedHtml ? (
          <Box
            sx={{
              mt: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              p: 2,
              bgcolor: "#fafafa",
              maxHeight: 420,
              overflow: "auto",
              fontSize: 14,
              "& table": { borderCollapse: "collapse", width: "100%" },
              "& img": { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: forwardedHtml }}
          />
        ) : (
          <Typography sx={{ color: "#777", mt: 2 }}>Cargando mensaje original…</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSending} sx={accentTextSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={sendForward}
          disabled={isSending}
          startIcon={
            isSending ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={accentBtnSx}
        >
          {isSending ? "Enviando..." : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
